import React from "react";
import { formatDate } from "../../utils/dateUtils";
import ActionMenu from "./ActionMenu";
/**
 * Extracts cell value from user object based on column key
 * @param {Object} user - User object
 * @param {string} columnKey - Column key identifier
 * @returns {string} Extracted value
 */
export function extractCellValue(user, columnKey) {
  switch (columnKey) {
    case "user": {
      const name =
        user.userName ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        "—";
      return name;
    }
    case "id":
      return user.id || "—";
    case "created":
      return user.userCreated ? formatDate(user.userCreated) : "—";
    case "email":
      return user.email || "—";
    case "role":
      return user.role || "—";
    case "studio":
      return user?.studioAccount?.status || "—";
    case "enterprise":
      return user?.enterpriseAccount?.status || "—";
    case "subscription": {
      const sub =
        user.subscriptionType ||
        (user.subscription && user.subscription.productId) ||
        "—";
      return sub;
    }
    case "platform":
      return user?.subscription?.platform || "—";
    case "subStartDate":
      return user?.subscription?.startDate
        ? formatDate(user.subscription.startDate)
        : "—";
    case "subEndDate":
      return user?.subscription?.endDate
        ? formatDate(user.subscription.endDate)
        : "—";
    case "credits":
      return String(user?.creditCount ?? "0");
    case "reportCredits":
      return String(user?.reportCredits ?? "0");
    case "dob":
      return user?.dob ? user.dob : "—";
    case "gender":
      return user?.gender || "—";
    case "dominantHand":
      return user?.dominantHand || "—";
    case "height":
      return user?.height || "—";
    case "coaches":
      return String(Array.isArray(user?.coaches) ? user.coaches.length : 0);
    case "students":
      return String(user?.students?.length ?? 0);
    case "sessions":
      return String(user?.sessionIds?.length ?? 0);
    case "secondarySubType":
      return user?.secondarySubType || "—";
    default:
      return "—";
  }
}

/**
 * Creates column definitions for the admin search table
 * @returns {Object} Column definitions object
 */
export function createUsersColumnDefinitions() {
  const renderHeader = (label) => <th className="px-2 py-3">{label}</th>;

  return {
    actions: {
      key: "actions",
      label: "Actions",
      renderHeader: () => <th className="px-0.5 py-3 text-center"></th>,
      renderCell: (u, handlers) => (
        <td className="px-0.5 py-3 text-center">
          <ActionMenu
            onEdit={() => handlers?.onEditUser?.(u.id)}
          />
        </td>
      ),
    },
    user: {
      key: "user",
      label: "User",
      renderHeader: () => <th className="px-2 py-3">User</th>,
      renderCell: (u) => {
        const name = extractCellValue(u, "user");
        return (
          <td className="px-2 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 min-w-9 items-center justify-center rounded-full bg-gray100 text-sm font-semibold text-gray700">
                {u.picture ? (
                  <img
                    src={u.picture}
                    alt="avatar"
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  String(name || "?")
                    .slice(0, 2)
                    .toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-gray900">{name}</div>
              </div>
            </div>
          </td>
        );
      },
    },

    id: {
      key: "id",
      label: "ID",
      renderHeader: () => renderHeader("ID"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "id")}</td>
      ),
    },
    created: {
      key: "created",
      label: "Created",
      renderHeader: () => renderHeader("Created"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "created")}</td>
      ),
    },
    email: {
      key: "email",
      label: "Email",
      renderHeader: () => renderHeader("Email"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "email")}</td>
      ),
    },
    role: {
      key: "role",
      label: "Role",
      renderHeader: () => renderHeader("Role"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "role")}</td>
      ),
    },
    studio: {
      key: "studio",
      label: "Studio",
      renderHeader: () => renderHeader("Studio"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "studio")}</td>
      ),
    },
    enterprise: {
      key: "enterprise",
      label: "Enterprise",
      renderHeader: () => renderHeader("Enterprise"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "enterprise")}</td>
      ),
    },
    subscription: {
      key: "subscription",
      label: "Subscription",
      renderHeader: () => renderHeader("Subscription"),
      renderCell: (u) => {
        return (
          <td className="px-2 py-3">{extractCellValue(u, "subscription")}</td>
        );
      },
    },
    platform: {
      key: "platform",
      label: "Platform",
      renderHeader: () => renderHeader("Platform"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "platform")}</td>
      ),
    },
    subStartDate: {
      key: "subStartDate",
      label: "Sub Start Date",
      renderHeader: () => renderHeader("Sub Start Date"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "subStartDate")}</td>
      ),
    },
    subEndDate: {
      key: "subEndDate",
      label: "Sub End Date",
      renderHeader: () => renderHeader("Sub End Date"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "subEndDate")}</td>
      ),
    },
    credits: {
      key: "credits",
      label: "Credits",
      renderHeader: () => renderHeader("Credits"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "credits")}</td>
      ),
    },
    reportCredits: {
      key: "reportCredits",
      label: "Report Credits",
      renderHeader: () => renderHeader("Report Credits"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "reportCredits")}</td>
      ),
    },
    dob: {
      key: "dob",
      label: "DOB",
      renderHeader: () => renderHeader("DOB"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "dob")}</td>
      ),
    },
    gender: {
      key: "gender",
      label: "Gender",
      renderHeader: () => renderHeader("Gender"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "gender")}</td>
      ),
    },
    dominantHand: {
      key: "dominantHand",
      label: "Dominant Hand",
      renderHeader: () => renderHeader("Dominant Hand"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "dominantHand")}</td>
      ),
    },
    height: {
      key: "height",
      label: "Height",
      renderHeader: () => renderHeader("Height"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "height")}</td>
      ),
    },
    coaches: {
      key: "coaches",
      label: "# of Coaches",
      renderHeader: () => renderHeader("# of Coaches"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "coaches")}</td>
      ),
    },
    students: {
      key: "students",
      label: "# of Students",
      renderHeader: () => renderHeader("# of Students"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "students")}</td>
      ),
    },
    sessions: {
      key: "sessions",
      label: "# of Sessions",
      renderHeader: () => renderHeader("# of Sessions"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "sessions")}</td>
      ),
    },
    secondarySubType: {
      key: "secondarySubType",
      label: "Secondary Sub Type",
      renderHeader: () => renderHeader("Secondary Sub Type"),
      renderCell: (u) => (
        <td className="px-2 py-3">{extractCellValue(u, "secondarySubType")}</td>
      ),
    },
  };
}
